import { useState } from 'react'

const AXIS_LABELS = ['-80', '-60', '-40', '-20', '0', '+20', '+40', '+60', '+80']
const BAR_MAX_HEIGHT = 80

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return [r, g, b]
}

function interpolateColor(hex1, hex2, t) {
  const [r1, g1, b1] = hexToRgb(hex1)
  const [r2, g2, b2] = hexToRgb(hex2)
  const r = Math.round(r1 + (r2 - r1) * t)
  const g = Math.round(g1 + (g2 - g1) * t)
  const b = Math.round(b1 + (b2 - b1) * t)
  return `rgb(${r}, ${g}, ${b})`
}

function winRateColor(rate) {
  if (rate === null) return '#e5e7eb'
  if (rate <= 0.5) {
    const t = rate / 0.5
    return interpolateColor('#ef4444', '#eab308', t)
  } else {
    const t = (rate - 0.5) / 0.5
    return interpolateColor('#eab308', '#22c55e', t)
  }
}

const LpzBracketChart = ({ data, title = 'Win Rate by Opponent LPZ' }) => {
  const [hovered, setHovered] = useState(null)

  if (!data || !data.buckets || data.buckets.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <p className="text-gray-500 italic">No LPZ bracket data available</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>

      {/* Bars */}
      <div className="relative flex items-end gap-0.5" style={{ height: `${BAR_MAX_HEIGHT}px` }}>
        {data.buckets.map((bucket, idx) => (
          <div
            key={idx}
            className="relative flex-1"
            data-testid="lpz-bracket-bucket"
            style={{ alignSelf: 'flex-end' }}
            onMouseEnter={() => setHovered(idx)}
            onMouseLeave={() => setHovered(null)}
          >
            {hovered === idx && (
              <div
                className="absolute bottom-full mb-2 left-1/2 z-10 pointer-events-none"
                style={{ transform: 'translateX(-50%)' }}
              >
                <div className="bg-white border border-gray-200 rounded shadow-md px-3 py-2 text-xs whitespace-nowrap">
                  <div className="font-semibold text-gray-800">{bucket.label}</div>
                  {bucket.matches > 0 ? (
                    <>
                      <div className="text-gray-600">{Math.round(bucket.win_rate * 100)}% win rate</div>
                      <div className="text-gray-500">{bucket.wins} W / {bucket.matches - bucket.wins} L ({bucket.matches} matches)</div>
                    </>
                  ) : (
                    <div className="text-gray-400">No matches</div>
                  )}
                </div>
              </div>
            )}
            <div
              style={{
                height: bucket.win_rate === null ? '3px' : `${Math.max(bucket.win_rate * BAR_MAX_HEIGHT, 3)}px`,
                backgroundColor: winRateColor(bucket.win_rate),
                borderRadius: '2px 2px 0 0',
                width: '100%',
              }}
            />
          </div>
        ))}
      </div>

      {/* Baseline */}
      <div className="border-t border-gray-300" />

      {/* Values below bars */}
      <div className="flex gap-0.5 mt-1">
        {data.buckets.map((bucket, idx) => (
          <div key={idx} className="flex-1 text-center">
            <span className="text-xs font-semibold text-gray-800">
              {bucket.win_rate === null ? '—' : `${Math.round(bucket.win_rate * 100)}%`}
            </span>
          </div>
        ))}
      </div>

      {/* Axis: boundary labels between bars */}
      <div className="relative mt-3" style={{ height: '14px' }}>
        {AXIS_LABELS.map((label, i) => (
          <span
            key={label}
            className="absolute text-xs text-gray-400 -translate-x-1/2"
            style={{ left: `${(i + 1) * 10}%` }}
          >
            {label}
          </span>
        ))}
      </div>
    </div>
  )
}

export default LpzBracketChart
